import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-js.css';
import './color-picker/color-picker.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css';
import { useMantineColorScheme } from '@mantine/core';
import BpmnModeler from 'bpmn-js/lib/Modeler';
// @ts-expect-error
import { CreateAppendAnythingModule } from 'bpmn-js-create-append-anything';

import * as React from 'react';
import { useEffect, useRef } from 'react';
import { BpmnColorPickerModule } from './color-picker';
import { CustomContextPadModule } from './context-pad';
import { CustomPaletteModule } from './palette';
import { ActivitiesReferenceContextPadModule } from './reference/activity-reference/activity-reference-contextpad';
import { ActivitiesReferenceContextPaletteModule } from './reference/activity-reference/activity-reference-palette';
import refExtension from './reference/ref.json';
import { ReferenceRendererModule } from './reference-renderer';
import { CustomRendererModule } from './renderer';
import { ResizeModule } from './resize';
import VersionModule from './VersionService';

export const BPMNModeler = ({
    bpmnModelerNeeded,
    setBPMNModeler,
    versionId,
}: {
    bpmnModelerNeeded: boolean;
    setBPMNModeler: (modelerInstance: BpmnModeler | null) => void;
    versionId?: string;
}) => {
    const bpmnModelerRef = useRef<BpmnModeler | null>(null);
    const containerRef = React.createRef<HTMLDivElement>();
    const { colorScheme } = useMantineColorScheme();
    const dark = colorScheme === 'dark';

    useEffect(() => {
        if (!containerRef.current || bpmnModelerRef.current) {
            return;
        }

        bpmnModelerRef.current = new BpmnModeler({
            container: containerRef.current,
            bpmnRenderer: {
                defaultFillColor: 'white',
                defaultStrokeColor: 'var(--mantine-color-text)',
            },
            keyboard: {
                bindTo: document,
            },
            moddleExtensions: {
                pvt: refExtension,
            },
            additionalModules:
                process.env.REACT_APP_NON_FILTERED_BPMN_ELEMENTS === 'true' ?
                    [
                        CustomPaletteModule,
                        CustomContextPadModule,
                        BpmnColorPickerModule,
                        ActivitiesReferenceContextPadModule,
                        ActivitiesReferenceContextPaletteModule,
                        ResizeModule,
                        CreateAppendAnythingModule,
                        CustomRendererModule,
                        ReferenceRendererModule,
                        VersionModule,
                    ]
                :   [
                        CustomPaletteModule,
                        CustomContextPadModule,
                        BpmnColorPickerModule,
                        ActivitiesReferenceContextPadModule,
                        ActivitiesReferenceContextPaletteModule,
                        ResizeModule,
                        CustomRendererModule,
                        ReferenceRendererModule,
                        VersionModule,
                    ],
            taskResizingEnabled: true,
            eventResizingEnabled: true,
            gatewayResizingEnabled: true,
            versionId,
        });
    }, [containerRef.current]);

    useEffect(() => {
        if (bpmnModelerNeeded && bpmnModelerRef.current) {
            setBPMNModeler(bpmnModelerRef.current);
        }
        return () => {
            const bpmnModeler = bpmnModelerRef.current;
            if (!bpmnModelerNeeded && bpmnModeler) {
                bpmnModelerRef.current = null;
                setBPMNModeler(null);
                bpmnModeler.detach();
                bpmnModeler.destroy();
            }
        };
    }, [bpmnModelerNeeded, bpmnModelerRef.current]);

    return (
        <div style={{ height: '100%' }} className={`modeler-parent ${dark ? 'dark-model' : 'light-model'}`}>
            <div id="modeler-container" style={{ height: '100%' }} ref={containerRef}></div>
        </div>
    );
};
